import { useState, useEffect } from 'react';
import {
  Sparkles,
  Loader2,
  Clock,
  Mail,
  Webhook,
  Check,
  Lightbulb,
  ChevronRight,
  Play,
  Eye,
  Database,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bell,
  Zap,
  FileText,
  User,
  Calendar,
  DollarSign,
  Send,
  CheckCircle2,
  XCircle,
  Table,
  Code,
  RefreshCw,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// Badge imported but currently unused - keeping for future use
// import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface AgentConfig {
  name: string;
  description: string;
  trigger: {
    type: 'cron' | 'webhook' | 'manual';
    cronExpression?: string;
  };
  condition: {
    type: 'sql_changed' | 'sql_threshold' | 'always';
    sql?: string;
  };
  action: {
    type: 'email' | 'webhook';
    email?: {
      to: string[];
      subject: string;
      bodyTemplate?: string;
    };
    webhook?: {
      url: string;
      method: 'POST' | 'GET';
    };
  };
}

interface AgentBuilderProps {
  onSubmit: (config: AgentConfig) => void;
  onCancel: () => void;
  projectId?: string;
  buildId?: number;
  onTestQuery?: (sql: string) => Promise<{ columns: string[]; rows: unknown[][]; error?: string }>;
}

const EXAMPLE_PROMPTS = [
  {
    text: "Email me when a sales order over $10,000 is confirmed",
    icon: DollarSign,
    category: "Sales",
  },
  {
    text: "Email notification when product inventory drops below 10 units",
    icon: Bell,
    category: "Inventory",
  },
  {
    text: "Send weekly sales report every Monday at 9am",
    icon: Calendar,
    category: "Reports",
  },
  {
    text: "Alert me when an invoice is overdue by more than 7 days",
    icon: AlertCircle,
    category: "Finance",
  },
  {
    text: "Notify when a new VIP customer is created",
    icon: User,
    category: "CRM",
  },
];

const SCHEDULE_OPTIONS = [
  {
    value: '*/5 * * * *',
    label: 'Every 5 minutes',
    description: 'Best for time-sensitive alerts like large orders',
    example: 'You\'ll be notified within 5 minutes of a new order',
    category: 'frequent',
  },
  {
    value: '*/15 * * * *',
    label: 'Every 15 minutes',
    description: 'Good balance of speed and efficiency',
    example: 'Checks 4 times per hour',
    category: 'frequent',
  },
  {
    value: '0 * * * *',
    label: 'Every hour',
    description: 'For less urgent monitoring',
    example: 'Low stock alerts, status changes',
    category: 'regular',
  },
  {
    value: '0 9 * * *',
    label: 'Daily at 9am',
    description: 'Perfect for daily reports and summaries',
    example: 'Morning overview of yesterday\'s activity',
    category: 'daily',
  },
  {
    value: '0 18 * * *',
    label: 'Daily at 6pm',
    description: 'End of day summary',
    example: 'Review before leaving for the day',
    category: 'daily',
  },
  {
    value: '0 9 * * 1',
    label: 'Weekly on Monday',
    description: 'For weekly reports and reviews',
    example: 'Last week\'s sales performance',
    category: 'weekly',
  },
  {
    value: '0 9 1 * *',
    label: 'Monthly (1st)',
    description: 'Monthly summaries and reports',
    example: 'Monthly revenue, overdue invoices',
    category: 'monthly',
  },
];

type Step = 'describe' | 'configure' | 'preview' | 'confirm';

const STEPS: { id: Step; label: string; icon: React.ElementType }[] = [
  { id: 'describe', label: 'Describe', icon: FileText },
  { id: 'configure', label: 'Configure', icon: Zap },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'confirm', label: 'Confirm', icon: Check },
];

interface PreviewData {
  columns: string[];
  rows: unknown[][];
  error?: string;
}

interface AvailableColumn {
  name: string;
  type: string;
  selected: boolean;
}

export function AgentBuilder({ onSubmit, onCancel, onTestQuery }: AgentBuilderProps) {
  const [currentStep, setCurrentStep] = useState<Step>('describe');
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [sqlViewMode, setSqlViewMode] = useState<'table' | 'sql'>('table');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [availableColumns, setAvailableColumns] = useState<AvailableColumn[]>([]);

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);

  const goToStep = (step: Step) => {
    setCurrentStep(step);
  };

  const goNext = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex].id);
    }
  };

  const goBack = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
    }
  };

  const handleParsePrompt = async () => {
    if (!prompt.trim()) return;

    setIsProcessing(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    const mockConfig: AgentConfig = {
      name: prompt.length > 50 ? prompt.slice(0, 50) + '...' : prompt,
      description: prompt,
      trigger: {
        type: 'cron',
        cronExpression: '*/5 * * * *',
      },
      condition: {
        type: 'sql_changed',
        sql: `SELECT
  so.id,
  so.name as order_number,
  so.date_order,
  so.amount_total,
  so.state,
  rp.name as customer_name,
  rp.email as customer_email
FROM sale_order so
JOIN res_partner rp ON so.partner_id = rp.id
WHERE so.state = 'sale'
  AND so.amount_total > 10000
ORDER BY so.date_order DESC
LIMIT 100`,
      },
      action: {
        type: 'email',
        email: {
          to: [''],
          subject: 'New Large Order: {{order_number}} - ${{amount_total}}',
          bodyTemplate: 'A new large sales order has been confirmed:\n\n**Order:** {{order_number}}\n**Customer:** {{customer_name}}\n**Amount:** ${{amount_total}}\n**Date:** {{date_order}}\n\n[View Order in Odoo]({{odoo_url}})',
        },
      },
    };

    if (prompt.toLowerCase().includes('webhook')) {
      mockConfig.action = {
        type: 'webhook',
        webhook: { url: '', method: 'POST' },
      };
    }
    if (prompt.toLowerCase().includes('weekly') || prompt.toLowerCase().includes('monday')) {
      mockConfig.trigger.cronExpression = '0 9 * * 1';
    }
    if (prompt.toLowerCase().includes('daily')) {
      mockConfig.trigger.cronExpression = '0 9 * * *';
    }
    if (prompt.toLowerCase().includes('hourly') || prompt.toLowerCase().includes('every hour')) {
      mockConfig.trigger.cronExpression = '0 * * * *';
    }
    if (prompt.toLowerCase().includes('inventory') || prompt.toLowerCase().includes('stock')) {
      mockConfig.condition.sql = `SELECT
  pp.id,
  pt.name as product_name,
  sq.quantity as qty_on_hand,
  pt.list_price
FROM product_product pp
JOIN product_template pt ON pp.product_tmpl_id = pt.id
LEFT JOIN stock_quant sq ON sq.product_id = pp.id
WHERE sq.quantity < 10
ORDER BY sq.quantity ASC
LIMIT 50`;
    }
    if (prompt.toLowerCase().includes('invoice') || prompt.toLowerCase().includes('overdue')) {
      mockConfig.condition.sql = `SELECT
  am.id,
  am.name as invoice_number,
  am.invoice_date,
  am.invoice_date_due,
  am.amount_total,
  am.amount_residual as amount_due,
  rp.name as customer_name
FROM account_move am
JOIN res_partner rp ON am.partner_id = rp.id
WHERE am.move_type = 'out_invoice'
  AND am.payment_state != 'paid'
  AND am.invoice_date_due < CURRENT_DATE - INTERVAL '7 days'
ORDER BY am.invoice_date_due ASC
LIMIT 50`;
      mockConfig.action.email = {
        to: [''],
        subject: 'Overdue Invoice Alert: {{invoice_number}}',
        bodyTemplate: 'The following invoice is overdue:\n\n**Invoice:** {{invoice_number}}\n**Customer:** {{customer_name}}\n**Amount Due:** ${{amount_due}}\n**Due Date:** {{invoice_date_due}}\n\nPlease follow up with the customer.',
      };
    }

    setConfig(mockConfig);
    setIsProcessing(false);
    goNext();
  };

  const loadPreviewData = async () => {
    if (!config?.condition.sql || !onTestQuery) {
      const mockData = {
        columns: ['id', 'order_number', 'customer_name', 'amount_total', 'date_order', 'state'],
        rows: [
          [1042, 'SO/2024/1042', 'Acme Corporation', 15750.00, '2024-01-15', 'sale'],
          [1038, 'SO/2024/1038', 'TechStart Inc', 12300.00, '2024-01-14', 'sale'],
          [1035, 'SO/2024/1035', 'Global Industries', 28900.00, '2024-01-13', 'sale'],
        ],
      };
      setPreviewData(mockData);
      setAvailableColumns(mockData.columns.map(col => ({ name: col, type: 'text', selected: true })));
      return;
    }

    setIsLoadingPreview(true);
    try {
      const result = await onTestQuery(config.condition.sql);
      setPreviewData(result);
      if (result.columns) {
        setAvailableColumns(result.columns.map(col => ({ name: col, type: 'text', selected: true })));
      }
    } catch (error) {
      setPreviewData({
        columns: [],
        rows: [],
        error: String(error),
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  useEffect(() => {
    if ((currentStep === 'configure' || currentStep === 'preview') && config && !previewData && !isLoadingPreview) {
      loadPreviewData();
    }
  }, [currentStep, config]);

  const updateConfig = (updates: Partial<AgentConfig>) => {
    if (config) {
      setConfig({ ...config, ...updates });
    }
  };

  const getScheduleOption = (cron?: string) => {
    return SCHEDULE_OPTIONS.find(p => p.value === cron);
  };

  const interpolateTemplate = (template: string) => {
    if (!previewData) return template;

    let result = template;
    previewData.columns.forEach((col, i) => {
      const value = previewData.rows[0]?.[i];
      result = result.replace(new RegExp(`\\{\\{${col}\\}\\}`, 'g'), String(value ?? ''));
    });
    result = result.replace(/\{\{odoo_url\}\}/g, 'https://your-odoo.com/web#id=1042&model=sale.order');
    return result;
  };

  const renderEmailPreviewContent = () => {
    if (!config || config.action.type !== 'email') return null;

    const subject = interpolateTemplate(config.action.email?.subject || '');
    const body = interpolateTemplate(config.action.email?.bodyTemplate || '');

    return (
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="bg-gray-100 px-4 py-3 border-b">
          <div className="space-y-1">
            <div className="flex gap-2 text-sm">
              <span className="text-muted-foreground w-16">To:</span>
              <span>{config.action.email?.to?.filter(Boolean).join(', ') || 'your-email@company.com'}</span>
            </div>
            <div className="flex gap-2 text-sm">
              <span className="text-muted-foreground w-16">Subject:</span>
              <span className="font-medium">{subject || '(No subject)'}</span>
            </div>
          </div>
        </div>
        <div className="p-4 text-sm whitespace-pre-wrap bg-white min-h-[150px]">
          {body.split('\n').map((line, i) => {
            if (line.startsWith('**') && line.endsWith('**')) {
              return <p key={i} className="font-bold">{line.replace(/\*\*/g, '')}</p>;
            }
            if (line.startsWith('[') && line.includes('](')) {
              const match = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
              if (match) {
                return (
                  <p key={i}>
                    <a href="#" className="text-blue-600 underline">{match[1]}</a>
                  </p>
                );
              }
            }
            return <p key={i}>{line || '\u00A0'}</p>;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Step Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/30">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = index < stepIndex;
          const isClickable = index <= stepIndex;

          return (
            <div key={step.id} className="flex items-center flex-1">
              <button
                onClick={() => isClickable && goToStep(step.id)}
                disabled={!isClickable}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-all",
                  isActive && "bg-primary text-primary-foreground",
                  isCompleted && !isActive && "text-primary",
                  !isActive && !isCompleted && "text-muted-foreground",
                  isClickable && !isActive && "hover:bg-muted cursor-pointer",
                  !isClickable && "cursor-not-allowed opacity-50"
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  isActive && "bg-primary-foreground/20",
                  isCompleted && !isActive && "bg-primary/10",
                  !isActive && !isCompleted && "bg-muted"
                )}>
                  {isCompleted ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <StepIcon className="h-4 w-4" />
                  )}
                </div>
                <span className="text-sm font-medium">{step.label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground mx-3 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Step 1: Describe */}
        {currentStep === 'describe' && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <h2 className="text-2xl font-semibold">What should this agent do?</h2>
              <p className="text-muted-foreground">
                Describe in plain English what you want to monitor and how you want to be notified.
              </p>
            </div>

            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Example: Email me when a sales order over $10,000 is confirmed..."
              className="min-h-[140px] text-base resize-none"
              autoFocus
            />

            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">Quick examples:</p>
              <div className="grid gap-2">
                {EXAMPLE_PROMPTS.map((example) => {
                  const ExampleIcon = example.icon;
                  return (
                    <button
                      key={example.text}
                      onClick={() => setPrompt(example.text)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border text-left transition-all hover:border-primary hover:bg-primary/5",
                        prompt === example.text && "border-primary bg-primary/5"
                      )}
                    >
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <ExampleIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{example.text}</p>
                        <p className="text-xs text-muted-foreground">{example.category}</p>
                      </div>
                      {prompt === example.text && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Configure */}
        {currentStep === 'configure' && config && (
          <div className="space-y-6 max-w-2xl mx-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">We've interpreted your request</p>
                  <p className="mt-1">Review and customize below. Changes update the preview instantly.</p>
                </div>
              </div>

              {/* Agent Name */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Agent Name</Label>
                <Input
                  value={config.name}
                  onChange={(e) => updateConfig({ name: e.target.value })}
                  placeholder="Give your agent a memorable name"
                />
              </div>

              <Separator />

              {/* When to Check - Improved */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">When to Check</h3>
                    <p className="text-sm text-muted-foreground">How often should this agent look for changes?</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p><strong>Tip:</strong> For urgent alerts (new orders, low stock), use "Every 5-15 minutes". For reports and summaries, "Daily" or "Weekly" works best.</p>
                  </div>
                </div>

                <Select
                  value={config.trigger.cronExpression}
                  onValueChange={(value) => updateConfig({
                    trigger: { ...config.trigger, cronExpression: value }
                  })}
                >
                  <SelectTrigger className="w-full h-auto py-3">
                    <SelectValue>
                      {getScheduleOption(config.trigger.cronExpression) ? (
                        <div className="text-left">
                          <p className="font-medium">{getScheduleOption(config.trigger.cronExpression)?.label}</p>
                          <p className="text-xs text-muted-foreground">{getScheduleOption(config.trigger.cronExpression)?.description}</p>
                        </div>
                      ) : (
                        <span>Select frequency</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <div className="py-1">
                      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">For Urgent Alerts</p>
                      {SCHEDULE_OPTIONS.filter(o => o.category === 'frequent').map((option) => (
                        <SelectItem key={option.value} value={option.value} className="py-2">
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-xs text-muted-foreground">{option.example}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                    <Separator className="my-1" />
                    <div className="py-1">
                      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Regular Checks</p>
                      {SCHEDULE_OPTIONS.filter(o => o.category === 'regular').map((option) => (
                        <SelectItem key={option.value} value={option.value} className="py-2">
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-xs text-muted-foreground">{option.example}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                    <Separator className="my-1" />
                    <div className="py-1">
                      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Daily Reports</p>
                      {SCHEDULE_OPTIONS.filter(o => o.category === 'daily').map((option) => (
                        <SelectItem key={option.value} value={option.value} className="py-2">
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-xs text-muted-foreground">{option.example}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                    <Separator className="my-1" />
                    <div className="py-1">
                      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Weekly & Monthly</p>
                      {SCHEDULE_OPTIONS.filter(o => o.category === 'weekly' || o.category === 'monthly').map((option) => (
                        <SelectItem key={option.value} value={option.value} className="py-2">
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-xs text-muted-foreground">{option.example}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Data to Monitor - With Tabs */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Database className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Data to Monitor</h3>
                    <p className="text-sm text-muted-foreground">The query that defines what triggers this agent</p>
                  </div>
                </div>

                <Tabs value={sqlViewMode} onValueChange={(v) => setSqlViewMode(v as 'table' | 'sql')}>
                  <div className="flex items-center justify-between">
                    <TabsList>
                      <TabsTrigger value="table" className="gap-2">
                        <Table className="h-4 w-4" />
                        Results
                      </TabsTrigger>
                      <TabsTrigger value="sql" className="gap-2">
                        <Code className="h-4 w-4" />
                        SQL Query
                      </TabsTrigger>
                    </TabsList>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadPreviewData}
                      disabled={isLoadingPreview}
                      className="gap-2"
                    >
                      {isLoadingPreview ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Run Query
                    </Button>
                  </div>

                  <TabsContent value="table" className="mt-3">
                    {isLoadingPreview ? (
                      <div className="flex items-center justify-center py-8 border rounded-lg bg-muted/30">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : previewData?.error ? (
                      <div className="p-4 border border-red-200 bg-red-50 rounded-lg flex gap-3">
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-800">Query Error</p>
                          <p className="text-sm text-red-700 mt-1">{previewData.error}</p>
                        </div>
                      </div>
                    ) : previewData && previewData.rows.length > 0 ? (
                      <div className="space-y-3">
                        <div className="bg-green-50 px-3 py-2 rounded-lg border border-green-200 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-800">
                            Found {previewData.rows.length} matching record{previewData.rows.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                          <div className="overflow-x-auto max-h-[200px]">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50 sticky top-0">
                                <tr>
                                  {previewData.columns.map((col, i) => (
                                    <th key={i} className="px-3 py-2 text-left font-medium border-b whitespace-nowrap">{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {previewData.rows.slice(0, 5).map((row, i) => (
                                  <tr key={i} className="hover:bg-muted/30">
                                    {row.map((cell, j) => (
                                      <td key={j} className="px-3 py-2 border-b truncate max-w-[150px]">
                                        {cell === null ? <span className="text-muted-foreground">null</span> : String(cell)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {previewData.rows.length > 5 && (
                            <div className="px-3 py-2 bg-muted/30 text-xs text-muted-foreground">
                              Showing 5 of {previewData.rows.length} results
                            </div>
                          )}
                        </div>

                        {/* Column Selector */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">Columns to include:</p>
                          <div className="flex flex-wrap gap-2">
                            {availableColumns.map((col) => (
                              <label
                                key={col.name}
                                className={cn(
                                  "flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer transition-colors",
                                  col.selected ? "bg-primary/10 border-primary text-primary" : "hover:bg-muted"
                                )}
                              >
                                <Checkbox
                                  checked={col.selected}
                                  onCheckedChange={(checked) => {
                                    setAvailableColumns(prev =>
                                      prev.map(c => c.name === col.name ? { ...c, selected: !!checked } : c)
                                    );
                                  }}
                                  className="h-3 w-3"
                                />
                                {col.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6 border rounded-lg bg-muted/30 text-center">
                        <Database className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No results yet</p>
                        <Button variant="outline" size="sm" onClick={loadPreviewData} className="mt-3 gap-2">
                          <Play className="h-4 w-4" />
                          Run Query
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="sql" className="mt-3">
                    <Textarea
                      value={config.condition.sql || ''}
                      onChange={(e) => updateConfig({
                        condition: { ...config.condition, sql: e.target.value },
                      })}
                      className="font-mono text-sm min-h-[200px]"
                      placeholder="SELECT columns FROM table WHERE condition..."
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      The agent will notify you when new rows appear or when results change.
                    </p>
                  </TabsContent>
                </Tabs>
              </div>

              <Separator />

              {/* How to Notify */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">How to Notify</h3>
                    <p className="text-sm text-muted-foreground">Choose how you want to receive alerts</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { type: 'email' as const, icon: Mail, label: 'Email' },
                    { type: 'webhook' as const, icon: Webhook, label: 'Webhook' },
                  ].map((option) => {
                    const OptionIcon = option.icon;
                    const isSelected = config.action.type === option.type;
                    return (
                      <button
                        key={option.type}
                        onClick={() => {
                          if (option.type === 'email') {
                            updateConfig({
                              action: {
                                type: 'email',
                                email: config.action.email || { to: [''], subject: '', bodyTemplate: '' },
                              },
                            });
                          } else {
                            updateConfig({
                              action: {
                                type: 'webhook',
                                webhook: config.action.webhook || { url: '', method: 'POST' },
                              },
                            });
                          }
                        }}
                        className={cn(
                          "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                          isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                        )}
                      >
                        <OptionIcon className={cn("h-5 w-5", isSelected ? "text-primary" : "text-muted-foreground")} />
                        <span className={cn("font-medium text-sm", isSelected && "text-primary")}>{option.label}</span>
                      </button>
                    );
                  })}
                </div>

                {config.action.type === 'email' && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-2">
                      <Label>Recipients</Label>
                      <Input
                        value={config.action.email?.to?.join(', ') || ''}
                        onChange={(e) => updateConfig({
                          action: {
                            ...config.action,
                            email: {
                              ...config.action.email!,
                              to: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                            },
                          },
                        })}
                        placeholder="email@example.com, another@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Subject Line</Label>
                      <Input
                        value={config.action.email?.subject || ''}
                        onChange={(e) => updateConfig({
                          action: {
                            ...config.action,
                            email: {
                              ...config.action.email!,
                              subject: e.target.value,
                            },
                          },
                        })}
                        placeholder="Alert: {{field_name}}"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Body</Label>
                      <Textarea
                        value={config.action.email?.bodyTemplate || ''}
                        onChange={(e) => updateConfig({
                          action: {
                            ...config.action,
                            email: {
                              ...config.action.email!,
                              bodyTemplate: e.target.value,
                            },
                          },
                        })}
                        placeholder="Write your email content here..."
                        className="min-h-[100px]"
                      />
                      <p className="text-xs text-muted-foreground">
                        Use {"{{column_name}}"} to insert values from query results
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setShowEmailPreview(true)}
                    >
                      <Eye className="h-4 w-4" />
                      Preview Email
                    </Button>
                  </div>
                )}

                {config.action.type === 'webhook' && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-2">
                      <Label>Webhook URL</Label>
                      <Input
                        value={config.action.webhook?.url || ''}
                        onChange={(e) => updateConfig({
                          action: {
                            ...config.action,
                            webhook: {
                              ...config.action.webhook!,
                              url: e.target.value,
                            },
                          },
                        })}
                        placeholder="https://api.example.com/webhook"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>HTTP Method</Label>
                      <Select
                        value={config.action.webhook?.method || 'POST'}
                        onValueChange={(value: 'POST' | 'GET') => updateConfig({
                          action: {
                            ...config.action,
                            webhook: {
                              ...config.action.webhook!,
                              method: value,
                            },
                          },
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="POST">POST</SelectItem>
                          <SelectItem value="GET">GET</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {currentStep === 'preview' && config && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                <Eye className="h-7 w-7 text-purple-600" />
              </div>
              <h2 className="text-2xl font-semibold">Preview Your Agent</h2>
              <p className="text-muted-foreground">
                See how your agent will work and what notifications will look like.
              </p>
            </div>

            {/* Summary Card */}
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{config.name || 'Unnamed Agent'}</p>
                  <p className="text-sm text-muted-foreground truncate">{config.description}</p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Schedule</p>
                  <p className="text-sm text-muted-foreground">{getScheduleOption(config.trigger.cronExpression)?.label || 'Not set'}</p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Database className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Data Query</p>
                  <p className="text-sm text-muted-foreground">
                    {previewData?.rows.length || 0} records currently match
                  </p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  {config.action.type === 'email' && <Mail className="h-5 w-5 text-green-600" />}
                  {config.action.type === 'webhook' && <Webhook className="h-5 w-5 text-green-600" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium capitalize">{config.action.type} Notification</p>
                  <p className="text-sm text-muted-foreground">
                    {config.action.type === 'email' && (config.action.email?.to?.filter(Boolean).join(', ') || 'No recipients set')}
                    {config.action.type === 'webhook' && (config.action.webhook?.url || 'No URL set')}
                  </p>
                </div>
              </div>
            </div>

            {/* Notification Preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium">Notification Preview</p>
                {config.action.type === 'email' && (
                  <Button variant="outline" size="sm" onClick={() => setShowEmailPreview(true)} className="gap-2">
                    <Eye className="h-4 w-4" />
                    Full Preview
                  </Button>
                )}
              </div>
              {config.action.type === 'email' && renderEmailPreviewContent()}
              {config.action.type === 'webhook' && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 border-b flex items-center gap-2 text-sm">
                    <Webhook className="h-4 w-4" />
                    <span className="font-mono">
                      {config.action.webhook?.method || 'POST'} {config.action.webhook?.url || 'https://...'}
                    </span>
                  </div>
                  <pre className="p-3 text-xs bg-gray-900 text-green-400 overflow-x-auto">
{`{
  "agent": "${config.name}",
  "data": ${JSON.stringify(
    previewData?.columns?.reduce((obj, col, i) => {
      obj[col] = previewData?.rows[0]?.[i] ?? null;
      return obj;
    }, {} as Record<string, unknown>) || {},
    null,
    2
  )}
}`}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Confirm */}
        {currentStep === 'confirm' && config && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold">Ready to Create Agent</h2>
              <p className="text-muted-foreground">
                Your agent is configured and ready to go
              </p>
            </div>

            <div className="border rounded-lg divide-y">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{config.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{config.description}</p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Schedule</p>
                  <p className="text-sm text-muted-foreground">{getScheduleOption(config.trigger.cronExpression)?.label}</p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Database className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Monitoring</p>
                  <p className="text-sm text-muted-foreground">{previewData?.rows.length || 0} records currently match</p>
                </div>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  {config.action.type === 'email' && <Mail className="h-5 w-5 text-green-600" />}
                  {config.action.type === 'webhook' && <Webhook className="h-5 w-5 text-green-600" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium capitalize">{config.action.type} Notification</p>
                  <p className="text-sm text-muted-foreground">
                    {config.action.type === 'email' && (config.action.email?.to?.filter(Boolean).join(', ') || 'No recipients')}
                    {config.action.type === 'webhook' && (config.action.webhook?.url || 'No URL')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">What happens next?</p>
                <p className="mt-1">
                  Your agent will start checking {getScheduleOption(config.trigger.cronExpression)?.label.toLowerCase()}.
                  You'll receive a notification whenever new records match your criteria.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t p-4 bg-background flex justify-between">
        <div>
          {currentStep !== 'describe' && (
            <Button variant="outline" onClick={goBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          {currentStep === 'describe' && (
            <Button
              onClick={handleParsePrompt}
              disabled={!prompt.trim() || isProcessing}
              className="gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
          {currentStep === 'configure' && (
            <Button onClick={goNext} className="gap-2">
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {currentStep === 'preview' && (
            <Button onClick={goNext} className="gap-2">
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {currentStep === 'confirm' && (
            <Button onClick={() => onSubmit(config!)} className="gap-2 bg-green-600 hover:bg-green-700">
              <Check className="h-4 w-4" />
              Create Agent
            </Button>
          )}
        </div>
      </div>

      {/* Email Preview Modal */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Preview
            </DialogTitle>
          </DialogHeader>
          {renderEmailPreviewContent()}
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEmailPreview(false)}>
              Close
            </Button>
            <Button className="gap-2">
              <Send className="h-4 w-4" />
              Send Test Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
